/**
 * Question Validation Functions
 * Validates question schema, checks for duplicates, calculates similarity
 */

/**
 * Validate a single question
 */
export function validateQuestion(question, index) {
  const errors = [];
  
  if (!question.question || question.question.trim().length < 10) {
    errors.push('Frage ist zu kurz (min. 10 Zeichen)');
  }
  
  if (!question.options || question.options.length < 2) {
    errors.push('Mindestens 2 Antwortoptionen erforderlich');
  }
  
  if (question.options && question.options.some(opt => !opt || opt.trim().length === 0)) {
    errors.push('Leere Antwortoptionen gefunden');
  }
  
  const optionsLength = question.options?.length || 0;
  
  if (question.allowMultiple || (question.correctAnswerIndices && question.correctAnswerIndices.length > 0)) {
    // Multiple choice validation
    if (!question.correctAnswerIndices || question.correctAnswerIndices.length === 0) {
      errors.push('Keine korrekten Antworten definiert für Multiple-Choice');
    } else {
      // Check if indices are valid
      const invalidIndices = question.correctAnswerIndices.filter(idx => 
        typeof idx !== 'number' || idx < 0 || idx >= optionsLength
      );
      if (invalidIndices.length > 0) {
        errors.push(`Ungültige correctAnswerIndices: ${invalidIndices.join(', ')}`);
      }
    }
  } else {
    // Single choice validation
    if (typeof question.correctAnswerIndex !== 'number' || 
        question.correctAnswerIndex < 0 || 
        question.correctAnswerIndex >= optionsLength) {
      errors.push('Ungültiger correctAnswerIndex');
    }
  }
  
  // Check for duplicate options
  if (question.options) {
    const uniqueOptions = new Set(question.options.map(o => o.toLowerCase().trim()));
    if (uniqueOptions.size !== question.options.length) {
      errors.push('Doppelte Antwortoptionen gefunden');
    }
  }
  
  return errors;
}

/**
 * Validate all questions in an array
 */
export function validateAllQuestions(questions) {
  const errors = {};
  questions.forEach((q, index) => {
    const questionErrors = validateQuestion(q, index);
    if (questionErrors.length > 0) {
      errors[index] = questionErrors;
    }
  });
  return { errors, isValid: Object.keys(errors).length === 0 };
}

/**
 * Find duplicate questions using similarity threshold
 */
export function findDuplicates(questions, threshold = 0.8) {
  const duplicates = [];
  for (let i = 0; i < questions.length; i++) {
    for (let j = i + 1; j < questions.length; j++) {
      const similarity = calculateSimilarity(
        questions[i].question.toLowerCase(),
        questions[j].question.toLowerCase()
      );
      if (similarity > threshold) {
        duplicates.push({ index1: i, index2: j, similarity });
      }
    }
  }
  return duplicates;
}

/**
 * Calculate text similarity using Levenshtein distance
 */
export function calculateSimilarity(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  if (longer.length === 0) return 1.0;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Calculate Levenshtein distance between two strings
 */
export function levenshteinDistance(str1, str2) {
  const matrix = [];
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[str2.length][str1.length];
}

/**
 * Get quality score for a question (0-100)
 */
export function getQualityScore(question) {
  let score = 100;
  const issues = [];

  // Check question length
  if (question.question.length < 20) {
    score -= 20;
    issues.push('Frage zu kurz');
  }

  // Check if all options are similar length (good practice)
  if (question.options) {
    const lengths = question.options.map(o => o.length);
    const avgLength = lengths.reduce((a, b) => a + b, 0) / lengths.length;
    const variance = lengths.reduce((sum, len) => sum + Math.pow(len - avgLength, 2), 0) / lengths.length;
    if (variance > 500) {
      score -= 10;
      issues.push('Antworten sehr unterschiedlich lang');
    }
  }

  // Check for code quality
  if (question.codeBlock?.code) {
    if (question.codeBlock.code.length > 500) {
      score -= 10;
      issues.push('Code-Block sehr lang');
    }
    if (!question.codeBlock.language) {
      score -= 15;
      issues.push('Programmiersprache fehlt');
    }
  }

  // Check number of options
  if (question.options && (question.options.length < 3 || question.options.length > 6)) {
    score -= 15;
    issues.push('Ungünstige Anzahl Optionen (3-5 ideal)');
  }

  return { score: Math.max(0, score), issues };
}
